// Firmas del trabajador (M45) → Checklist del Anexo 1: TH-06 (asistencia a inducciones), TH-09 (asistencia a
// capacitaciones y entrenamientos), PO-22 (entrega-recepción de EPP) y PO-04 (certificados de aptitud médica de
// ingreso y periódicos). Reglas DECIDIDAS POR CLAUDE, por validar con Kevin:
//  - Unidades que se miden en los últimos 12 meses (ventana POR VALIDAR: el Anexo 1 no fija frecuencia): TH-06 =
//    capacitaciones "Realizada" de tipo inducción; TH-09 = las de tipo capacitación o entrenamiento; PO-22 = cada
//    asignación de EPP; PO-04 = cada examen de ingreso o periódico.
//  - Cumple: todas las unidades tienen su registro firmado (en pantalla, completo, o con el escaneo firmado subido).
//  - No cumple: alguna unidad de hace más de 15 días (plazo para recoger las firmas, POR VALIDAR) sin registro firmado.
//  - En proceso: faltan registros firmados, pero todos dentro del plazo.
//  - Pendiente: no hay unidades en la ventana.
// Estado manual (misma regla que PO-14 a PO-18 y PO-23): un estado marcado a mano (auto_source NULL) nunca se
// sobrescribe; el Checklist muestra "el cálculo automático dice: X" y el botón "Usar cálculo automático". Nunca se toca
// un "No aplica". Solo corre para empresas que ya usan las firmas (tienen al menos un registro).

import { supabase } from '../../supabaseClient';
import type { Traza } from '../pe/pe-tipos';
import { esManual, type EstadoGuardadoChecklist, type EstadoItem, type TrazasPlanV2 } from '../plan-emergencias-compliance-sync';
import { plural } from '../plurales';

export const FUENTE_FIRMAS = 'firmas_trabajador';
export const ITEMS_FIRMAS = ['TH-06', 'TH-09', 'PO-04', 'PO-22'] as const;
export type ItemFirmas = typeof ITEMS_FIRMAS[number];
export const MODULO_FIRMAS: Record<ItemFirmas, string> = {
  'TH-06': 'Capacitaciones (registros firmados)', 'TH-09': 'Capacitaciones (registros firmados)',
  'PO-04': 'Vigilancia de la salud (certificados firmados)', 'PO-22': 'EPP (entregas firmadas)',
};
export const VENTANA_MESES_FIRMAS = 12;
export const PLAZO_DIAS_FIRMAS = 15;
export const NORMA_REGLA_FIRMAS = 'Criterio técnico de StorSafe (por validar)';
const FUENTE_ANEXO1 = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo)';

/** Una unidad que necesita registro firmado: su fecha y el estado de su registro (null = sin registro). */
export interface UnidadFirmable { fecha: string; estado: 'abierto' | 'en_firma' | 'firmado' | null }

export interface ResultadoItemFirmas { estado: EstadoItem; resumen: string; trazas: Traza[] }

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function restarMeses(d: Date, meses: number): string {
  const x = new Date(d.getFullYear(), d.getMonth() - meses, d.getDate());
  return iso(x);
}
function restarDias(d: Date, dias: number): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dias);
  return iso(x);
}

const estadoTraza = (e: EstadoItem): Traza['estado'] => (e === 'cumple' ? 'cumple' : e === 'no_cumple' ? 'no_cumple' : 'por_validar');

/** Regla común: unidades de la ventana, firmadas, fuera de plazo. */
export function resultadoFirmas(unidades: UnidadFirmable[], hoy: Date, articulo: string, que: { uno: string; varios: string }): ResultadoItemFirmas {
  const desde = restarMeses(hoy, VENTANA_MESES_FIRMAS);
  const hoyIso = iso(hoy);
  const limite = restarDias(hoy, PLAZO_DIAS_FIRMAS);
  const enVentana = unidades.filter(u => u.fecha && u.fecha >= desde && u.fecha <= hoyIso);
  const firmadas = enVentana.filter(u => u.estado === 'firmado').length;
  const faltan = enVentana.filter(u => u.estado !== 'firmado');
  const vencidas = faltan.filter(u => u.fecha < limite).length;
  const estado: EstadoItem = enVentana.length === 0 ? 'pendiente' : faltan.length === 0 ? 'cumple' : vencidas > 0 ? 'no_cumple' : 'en_proceso';
  const total = plural(enVentana.length, que.uno, que.varios);
  const resumen = estado === 'pendiente' ? `No hay ${que.varios} en los últimos ${VENTANA_MESES_FIRMAS} meses.`
    : estado === 'cumple' ? `${total} en los últimos ${VENTANA_MESES_FIRMAS} meses, con registro firmado en todos los casos.`
      : estado === 'no_cumple' ? `${plural(vencidas, `${que.uno} sin registro firmado`, `${que.varios} sin registro firmado`)} después de ${PLAZO_DIAS_FIRMAS} días (de ${total}).`
        : `${plural(faltan.length, `${que.uno} todavía sin firmar`, `${que.varios} todavía sin firmar`)}, dentro del plazo de ${PLAZO_DIAS_FIRMAS} días (de ${total}).`;
  return {
    estado, resumen, trazas: [
      { metodo: 'firmas_trabajador', norma: NORMA_REGLA_FIRMAS, articulo_o_tabla: articulo, umbral: enVentana.length, valor: firmadas,
        unidad: `registros firmados (${VENTANA_MESES_FIRMAS} meses, por validar)`, estado: estadoTraza(estado), fuente_doc: FUENTE_ANEXO1, nota: resumen },
      { metodo: 'firmas_trabajador', norma: NORMA_REGLA_FIRMAS, articulo_o_tabla: `Plazo para recoger las firmas: ${PLAZO_DIAS_FIRMAS} días`, umbral: 0,
        valor: vencidas, unidad: 'sin firmar fuera de plazo', estado: vencidas > 0 ? 'no_cumple' : 'cumple', fuente_doc: FUENTE_ANEXO1,
        nota: 'El registro cuenta si está firmado en pantalla o si se subió el formato impreso firmado.' },
    ],
  };
}

// ── Entradas mínimas ──
export interface CapacitacionMin { id: string; record_kind: string | null; status: string | null; scheduled_date: string }
export interface RegistroMin { id: string; kind: string; training_id: string | null; medical_exam_id: string | null; status: 'abierto' | 'en_firma' | 'firmado' }
export interface AsignacionMin { id: string; assigned_date: string }
export interface ItemEppMin { record_id: string; epp_assignment_id: string | null }
export interface ExamenMin { id: string; exam_type: string | null; exam_date: string }

export function resultadosFirmas(caps: CapacitacionMin[], registros: RegistroMin[], asignaciones: AsignacionMin[], itemsEpp: ItemEppMin[],
  examenes: ExamenMin[], hoy: Date = new Date()): Record<ItemFirmas, ResultadoItemFirmas> {
  const porCap = new Map(registros.filter(r => r.training_id).map(r => [r.training_id as string, r.status]));
  const porExamen = new Map(registros.filter(r => r.medical_exam_id).map(r => [r.medical_exam_id as string, r.status]));
  const estadoRegistro = new Map(registros.map(r => [r.id, r.status]));
  const porAsignacion = new Map(itemsEpp.filter(i => i.epp_assignment_id).map(i => [i.epp_assignment_id as string, estadoRegistro.get(i.record_id) ?? null]));
  const realizadas = caps.filter(c => c.status === 'realizada');
  const unidadCap = (c: CapacitacionMin): UnidadFirmable => ({ fecha: c.scheduled_date, estado: porCap.get(c.id) ?? null });
  return {
    'TH-06': resultadoFirmas(realizadas.filter(c => c.record_kind === 'induccion').map(unidadCap), hoy,
      'TH 6: registro de asistencia a inducciones (fecha, tema, trabajador, cédula, firmas, técnico, material y evaluación)',
      { uno: 'inducción realizada', varios: 'inducciones realizadas' }),
    'TH-09': resultadoFirmas(realizadas.filter(c => c.record_kind !== 'induccion').map(unidadCap), hoy,
      'TH 9: registro de asistencia a capacitaciones y entrenamientos (mismo contenido mínimo que TH 6)',
      { uno: 'capacitación realizada', varios: 'capacitaciones realizadas' }),
    'PO-22': resultadoFirmas(asignaciones.map(a => ({ fecha: a.assigned_date, estado: porAsignacion.get(a.id) ?? null })), hoy,
      'PO 22: registro de entrega-recepción de EPP (fecha, trabajador, cédula, detalle, firma y devoluciones)',
      { uno: 'entrega de EPP', varios: 'entregas de EPP' }),
    'PO-04': resultadoFirmas(examenes.filter(e => e.exam_type === 'pre_ocupacional' || e.exam_type === 'periodico')
      .map(e => ({ fecha: e.exam_date, estado: porExamen.get(e.id) ?? null })), hoy,
      'PO 4: certificados de aptitud médica de ingreso y periódicos (firma del trabajador y del médico)',
      { uno: 'examen de ingreso o periódico', varios: 'exámenes de ingreso o periódicos' }),
  };
}

// ¿Qué se escribe? null = nada. Lo manual (auto_source NULL) nunca se reemplaza; "No aplica" tampoco.
export function decisionFirmas(existente: EstadoGuardadoChecklist | undefined, deseado: EstadoItem): EstadoItem | null {
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null;
  if (existente?.estado === deseado && existente.auto_source === FUENTE_FIRMAS) return null;
  if (!existente && deseado === 'pendiente') return null;
  return deseado;
}

export async function syncFirmasCompliance(companyId: string, hoy: Date = new Date()): Promise<TrazasPlanV2> {
  if (!companyId) return {};
  const { data: regs, error } = await supabase.from('signed_records').select('id, kind, training_id, medical_exam_id, status').eq('company_id', companyId);
  // Sin M45 en la base (o sin permiso) o sin ningún registro: no se toca nada. Las socializaciones de procedimientos
  // (M49, GT 37) no cuentan como uso de estas firmas.
  if (error || !regs?.some(r => (r as { kind: string }).kind !== 'socializacion')) return {};
  const [{ data: caps, error: e1 }, { data: asig, error: e2 }, { data: items, error: e3 }, { data: exams, error: e4 }, { data: existentes }] = await Promise.all([
    supabase.from('sso_trainings').select('id, record_kind, status, scheduled_date').eq('company_id', companyId),
    supabase.from('epp_assignments').select('id, assigned_date').eq('company_id', companyId),
    supabase.from('signed_record_epp_items').select('record_id, epp_assignment_id').eq('company_id', companyId),
    supabase.from('worker_medical_exams').select('id, exam_type, exam_date').eq('company_id', companyId),
    supabase.from('checklist_item_estados').select('item_id, estado, auto_source').eq('company_id', companyId).in('item_id', [...ITEMS_FIRMAS]),
  ]);
  if (e1 || e2 || e3 || e4) return {};
  const res = resultadosFirmas((caps ?? []) as CapacitacionMin[], regs as RegistroMin[], (asig ?? []) as AsignacionMin[],
    (items ?? []) as ItemEppMin[], (exams ?? []) as ExamenMin[], hoy);
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const ahora = new Date().toISOString();
  const filas = ITEMS_FIRMAS.flatMap(item => {
    const d = decisionFirmas(porItem.get(item), res[item].estado);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_FIRMAS, updated_at: ahora }] : [];
  });
  if (filas.length) {
    const { error: eu } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (eu) console.error('syncFirmasCompliance', eu);
  }
  return Object.fromEntries(ITEMS_FIRMAS.map(item => {
    const previo = porItem.get(item);
    const manual = esManual(previo);
    return [item, { ...res[item], manualRespetado: manual, estadoManual: manual ? previo!.estado : null, modulo: MODULO_FIRMAS[item], fuente: FUENTE_FIRMAS }];
  }));
}

// "Usar cálculo automático": quita el estado manual y escribe el cálculo de las firmas. Nunca sobre "No aplica".
export async function aplicarCalculoFirmas(companyId: string, itemId: string, estado: EstadoItem): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', itemId).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de ${itemId}: ${error.message}`);
  if ((data as { estado: string } | null)?.estado === 'no_aplica') throw new Error(`${itemId} está marcado como "No aplica": no se cambia.`);
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: itemId, estado, auto_source: FUENTE_FIRMAS, updated_at: new Date().toISOString() },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(e2.code === '42501' ? 'No tienes permiso para cambiar el Checklist.' : `No se pudo aplicar el cálculo en ${itemId}: ${e2.message}`);
}
